import React, { useEffect, useMemo, useState } from "react";
import { Client } from "@/bryntum-lib/models/Client";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { clientSchema, ClientFormData } from "@/react/schema/clientSchema";
import { toast } from "sonner";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { ISwivuStore } from "@/types/SwivuStore";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { SwivuDuplicateEntityHelper } from "@/react/components/SwivuDuplicateEntityHelper";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { useConfirm } from "@omit/react-confirm-dialog";
import { EditorFormField } from "../components/EditorFormField";
import { EditorSection } from "../components/EditorSection";
import { EditorAuditFooter } from "../components/EditorAuditFooter";
import { formatAuditDate, type AuditItem } from "../utils/editorUtils";
import { EditorMetaDetails } from "../components/EditorMetaDetails";
import { EDITOR_META_ITEMS } from "../constants/editorMetaItems";
import { useWarRoom } from "@/react/hooks/useWarRoom";
import { EditorWarRoomToggle } from "../components/EditorWarRoomToggle";

interface ClientGeneralEditorProps {
  record: {
    client: Client;
    onAfterRecordAdd?: (clientRecord: Client) => void;
  };
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const clientMetaItems = EDITOR_META_ITEMS[ENTITY_TYPES.CLIENT];

export const ClientGeneralEditor = ({ record, onClose, editorInstance }: ClientGeneralEditorProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { project, entityManager } = useSwivuBryntumContext();
  const { isWarRoom, toggleWarRoom } = useWarRoom("client-editor");
  const { client: clientRecord, onAfterRecordAdd } = record;
  const { subscribe } = useSwivuStore<Client>({ storeId: STORE_KEYS.clientStore });
  const confirm = useConfirm();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: clientRecord.name || "",
      website: clientRecord.website || "",
      address: clientRecord.address || "",
      statusId: clientRecord.statusId || null,
      categoryId: clientRecord.categoryId || null,
      teamId: clientRecord.teamId || null,
      tagsId: clientRecord.tagsId || null,
    },
  });

  useEffect(() => {
    return subscribe(({ record: changedRecord }) => {
      if (clientRecord.id === changedRecord.id) {
        reset({
          name: changedRecord.name || "", website: changedRecord.website || "",
          address: changedRecord.address || "", statusId: changedRecord.statusId || null,
          categoryId: changedRecord.categoryId || null, teamId: changedRecord.teamId || null,
          tagsId: changedRecord.tagsId || null,
        });
        return true;
      }
      return false;
    });
  }, [clientRecord, reset, subscribe]);

  const onSubmit = async (data: ClientFormData) => {
    try {
      setIsSaving(true);
      clientRecord.set(data);
      if (clientRecord.isPhantom) {
        project.clientsStore.add(clientRecord);
        onAfterRecordAdd?.(clientRecord);
      }
      await project.sync();
      toast.success("Client saved successfully");
    } catch (error) {
      console.error("Error saving client:", error);
      toast.error("Something went wrong while saving the client. Please try again.", {
        description: "If the problem persists, please contact support.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => handleSubmit(onSubmit)();

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        alertDialogOverlay: { className: "z-[--swivu-bryntum-z-index]" },
        alertDialogContent: { className: "z-[--swivu-bryntum-z-index]" },
        customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">Keep working</Button>
            <Button variant="destructive" onClick={confirm} size="sm">Do not save</Button>
            <Button variant="success" size="sm" onClick={async () => {
              if (!isValid) { await trigger("name"); cancel(); return; }
              handleSave(); confirm();
            }}>Save & close</Button>
          </div>
        ),
      });
      if (confirmed) onClose();
      return;
    }
    onClose();
  };

  const openDashboard = () => {
    if (entityManager) {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: clientRecord.id });
    }
  };

  const handleSaveAndDashboard = async () => {
    await handleSave();
    if (isValid) { openDashboard(); onClose(); }
  };

  const hideDashboardButton = entityManager.isOpen && entityManager.entityId == clientRecord.id;

  const auditItems = useMemo<AuditItem[]>(() => {
    const items: AuditItem[] = [];
    if (clientRecord.creatorRecord?.name && clientRecord.createdAt)
      items.push({ key: "created", label: "Created", value: `${clientRecord.creatorRecord.name} - ${formatAuditDate(clientRecord.createdAt)}` });
    if (clientRecord.updaterRecord?.name && clientRecord.updatedAt)
      items.push({ key: "updated", label: "Updated", value: `${clientRecord.updaterRecord.name} - ${formatAuditDate(clientRecord.updatedAt)}` });
    return items;
  }, [clientRecord.createdAt, clientRecord.creatorRecord?.name, clientRecord.updatedAt, clientRecord.updaterRecord?.name]);

  const headerRenderer = () => (
    <div className="drag-handle flex items-center gap-4 p-5 border-b border-border/50 rounded-t-lg">
      <h2 className="text-xl font-bold tracking-tight text-foreground">General Settings</h2>
      <p className="text-sm text-muted-foreground">Clients can be customers, members, or any other stakeholder that tasks are performed for.</p>
      <div className="ml-auto">
        <EditorWarRoomToggle pressed={isWarRoom} onPressedChange={toggleWarRoom} />
      </div>
      <button
        onClick={handleClose}
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100 !size-12"
      >
        <i className="fa-light fa-times text-swivu-icon" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  return (
    <BaseModal
      record={clientRecord}
      onClose={handleClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{ width: "66rem", height: "min(40rem, 90vh)", maxHeight: "90vh", maxWidth: "90vw", minWidth: "66rem", minHeight: "min(40rem, 90vh)" }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      footerRenderer={() => (
        <div className="w-full">
          <div className="flex w-full flex-col gap-3 py-2 md:flex-row md:items-center md:justify-between">
            <EditorAuditFooter items={auditItems} isSaving={isSaving} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button onClick={handleSave} disabled={!isDirty || isSaving}>Save Changes</Button>
              {!hideDashboardButton && (
                <Button
                  type="button"
                  disabled={(clientRecord.isPhantom && !isDirty) || isSaving}
                  onClick={() => {
                    if (!isDirty && !clientRecord.isPhantom) { openDashboard(); onClose(); }
                    else { handleSaveAndDashboard(); }
                  }}
                >
                  Open Dashboard <i className="fa-solid fa-arrow-right -rotate-45" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      headerRenderer={headerRenderer}
      headerIconCls=""
      className={`bg-background resize-none overflow-visible ${editorInstance?.className || ""}`}
      style={{ zoom: isWarRoom ? 1.15 : 1 }}
    >
      <BaseModalBody contentElementClassName="bg-transparent" className="!p-0">
        <form className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Section 1 — Identity */}
            <EditorSection>
              {/* Client Name */}
              <EditorFormField label="Client Name" required error={errors.name?.message}>
                <Controller
                  name="name"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => {
                    const DuplicateHelperComponent = SwivuDuplicateEntityHelper as React.ComponentType<{
                      record: Client; store: ISwivuStore<Client>; placeholder: string; editorReactState: unknown;
                      onChange: (params: { value: string; record?: Client }) => void; disabled: boolean;
                      editorRecordKey: string; inputElementClassName: string; name: string; value: string; onBlur: () => void;
                    }>;
                    return (
                      <DuplicateHelperComponent
                        {...field}
                        record={clientRecord}
                        store={project.clientsStore}
                        placeholder="Enter client name"
                        editorReactState={editorInstance}
                        onChange={({ value, record: selectedRecord }: { value: string; record?: Client }) => {
                          if (selectedRecord) {
                            reset({
                              name: selectedRecord.name || "", website: selectedRecord.website || "",
                              address: selectedRecord.address || "", statusId: selectedRecord.statusId || null,
                              categoryId: selectedRecord.categoryId || null, teamId: selectedRecord.teamId || null,
                              tagsId: selectedRecord.tagsId || null,
                            });
                            if (clientRecord.isPhantom) onAfterRecordAdd?.(selectedRecord);
                          } else {
                            field.onChange(value);
                          }
                        }}
                        disabled={isSaving}
                        editorRecordKey="client"
                        inputElementClassName="flex w-full rounded-md text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-background h-swivu-input px-3 py-2"
                      />
                    );
                  }}
                />
              </EditorFormField>

              {/* Website + Address */}
              <div className="grid grid-cols-2 gap-5">
                <EditorFormField label="Website" htmlFor="website" error={errors.website?.message}>
                  <Controller
                    name="website"
                    control={control}
                    disabled={isSaving}
                    render={({ field }) => (
                      <Input
                        id="website"
                        variant="outline"
                        placeholder="Enter website URL"
                        autoComplete="off"
                        {...field}
                        className={errors.website ? "border-destructive" : ""}
                      />
                    )}
                  />
                </EditorFormField>

                <EditorFormField label="Address" htmlFor="address" error={errors.address?.message}>
                  <Controller
                    name="address"
                    control={control}
                    disabled={isSaving}
                    render={({ field }) => (
                      <Input
                        id="address"
                        variant="outline"
                        placeholder="Enter address"
                        autoComplete="off"
                        {...field}
                        className={errors.address ? "border-destructive" : ""}
                      />
                    )}
                  />
                </EditorFormField>
              </div>
            </EditorSection>

            {/* Section 2 — Classification & Tags */}
            <EditorSection withDivider>
              <EditorMetaDetails
                record={clientRecord}
                storeKey={STORE_KEYS.clientStore}
                metaItems={clientMetaItems}
                isDirectUpdates={false}
                onFieldChange={(changes) => setValue(changes.fieldName as keyof ClientFormData, changes.value, { shouldDirty: true })}
                isFieldDisabled={isSaving}
              />
            </EditorSection>

          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
};
